(function () {
  const OPEN_SELECTOR = '[data-vela-drawer-open]';
  const CLOSE_SELECTOR = '[data-vela-drawer-close]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

  const scrollLock = {
    owner: null,
    scrollY: 0,
    isMobileLock: false,
    scrollContainer: null,
    containerStyles: null,
    rootStyles: null,
    bodyStyles: null,
    mobileTouchY: 0,

    getMobileContent(event) {
      const target = event.target;
      if (!(target instanceof Element) || !this.owner?.activeLayer) return null;
      const content = target.closest('.vela-info-drawer__content');
      return content instanceof HTMLElement && this.owner.activeLayer.contains(content) ? content : null;
    },

    onMobileTouchStart(event) {
      scrollLock.mobileTouchY = event.touches[0]?.clientY || 0;
    },

    onMobileTouchMove(event) {
      const touch = event.touches[0];
      if (!touch) return;

      const content = scrollLock.getMobileContent(event);
      const movement = touch.clientY - scrollLock.mobileTouchY;
      scrollLock.mobileTouchY = touch.clientY;

      if (!content) {
        event.preventDefault();
        return;
      }

      const isAtTop = content.scrollTop <= 0;
      const isAtBottom = content.scrollTop + content.clientHeight >= content.scrollHeight - 1;
      const cannotScroll = content.scrollHeight <= content.clientHeight;

      if (cannotScroll || (movement > 0 && isAtTop) || (movement < 0 && isAtBottom)) {
        event.preventDefault();
      }
    },

    onMobileWheel(event) {
      const content = scrollLock.getMobileContent(event);
      if (!content) {
        event.preventDefault();
        return;
      }

      const isAtTop = content.scrollTop <= 0;
      const isAtBottom = content.scrollTop + content.clientHeight >= content.scrollHeight - 1;
      const cannotScroll = content.scrollHeight <= content.clientHeight;

      if (cannotScroll || (event.deltaY < 0 && isAtTop) || (event.deltaY > 0 && isAtBottom)) {
        event.preventDefault();
      }
    },

    lock(owner) {
      if (this.owner === owner) return;
      if (this.owner) this.owner.finishClose({ restoreFocus: false });

      const root = document.documentElement;
      const body = document.body;
      const isMobile = MOBILE_QUERY.matches;

      this.owner = owner;
      this.isMobileLock = isMobile;

      // On mobile, leave the document and the theme's scroll container exactly
      // where they are. Cancelling background gestures avoids the delayed
      // position restoration that causes a visible jump when the drawer closes.
      if (isMobile) {
        this.scrollY = window.scrollY;
        document.addEventListener('touchstart', this.onMobileTouchStart, { passive: true, capture: true });
        document.addEventListener('touchmove', this.onMobileTouchMove, { passive: false, capture: true });
        document.addEventListener('wheel', this.onMobileWheel, { passive: false, capture: true });
        body.classList.add('vela-info-drawer-scroll-locked');
        return;
      }

      // Ritual scrolls .page-wrapper on desktop instead of the document.
      // Locking that exact element preserves the theme's viewport structure.
      if (!isMobile) {
        const container = document.querySelector('.page-wrapper');
        if (container instanceof HTMLElement) {
          const beforeClientWidth = container.clientWidth;
          const computedPaddingRight = Number.parseFloat(getComputedStyle(container).paddingRight) || 0;

          this.scrollContainer = container;
          this.scrollY = container.scrollTop;
          this.containerStyles = {
            overflowY: container.style.overflowY,
            paddingRight: container.style.paddingRight,
            scrollbarGutter: container.style.scrollbarGutter,
          };

          container.style.scrollbarGutter = 'stable';
          container.style.overflowY = 'hidden';

          // Exact fallback for browsers that do not retain a stable gutter.
          const gainedWidth = Math.max(0, container.clientWidth - beforeClientWidth);
          if (gainedWidth > 0) {
            container.style.paddingRight = `${computedPaddingRight + gainedWidth}px`;
          }

          body.classList.add('vela-info-drawer-scroll-locked');
          return;
        }
      }

      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
      const bodyPaddingRight = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;

      this.scrollY = window.scrollY;
      this.rootStyles = {
        overflow: root.style.overflow,
        paddingRight: root.style.paddingRight,
        scrollbarGutter: root.style.scrollbarGutter,
        scrollBehavior: root.style.scrollBehavior,
      };
      this.bodyStyles = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
      };

      root.style.scrollbarGutter = 'stable';
      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';

      // Modern browsers retain the stable gutter. The exact measured fallback
      // keeps the body content width unchanged in browsers that do not.
      const retainedGutter = root.clientWidth === window.innerWidth - scrollbarWidth;
      if (scrollbarWidth > 0 && !retainedGutter) {
        body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
      }

      body.classList.add('vela-info-drawer-scroll-locked');
    },

    unlock(owner) {
      if (this.owner !== owner) return;

      const root = document.documentElement;
      const body = document.body;
      const restoreScroll = this.scrollY;
      const wasMobileLock = this.isMobileLock;
      const restoreScrollBehavior = this.rootStyles?.scrollBehavior || '';

      if (wasMobileLock) {
        document.removeEventListener('touchstart', this.onMobileTouchStart, true);
        document.removeEventListener('touchmove', this.onMobileTouchMove, true);
        document.removeEventListener('wheel', this.onMobileWheel, true);
      }

      if (this.scrollContainer) {
        Object.assign(this.scrollContainer.style, this.containerStyles || {});
        this.scrollContainer.scrollTop = restoreScroll;
      } else {
        if (wasMobileLock) root.style.scrollBehavior = 'auto';
        root.style.overflow = this.rootStyles?.overflow || '';
        root.style.paddingRight = this.rootStyles?.paddingRight || '';
        root.style.scrollbarGutter = this.rootStyles?.scrollbarGutter || '';
        Object.assign(body.style, this.bodyStyles || {});
      }
      body.classList.remove('vela-info-drawer-scroll-locked');

      this.owner = null;
      this.isMobileLock = false;
      this.scrollContainer = null;
      this.containerStyles = null;
      this.rootStyles = null;
      this.bodyStyles = null;

      if (wasMobileLock) root.style.scrollBehavior = restoreScrollBehavior;
    },
  };

  class VelaInformationDrawers extends HTMLElement {
    constructor() {
      super();
      this.activeLayer = null;
      this.activeTrigger = null;
      this.closeTimer = 0;
      this.abortController = null;
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.onClick, { signal });
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.finishClose({ restoreFocus: false });
    }

    onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const openButton = target.closest(OPEN_SELECTOR);
      if (openButton && this.contains(openButton)) {
        event.preventDefault();
        this.open(openButton.getAttribute('data-vela-drawer-open'), openButton);
        return;
      }

      const closeButton = target.closest(CLOSE_SELECTOR);
      if (closeButton && this.activeLayer?.contains(closeButton)) {
        event.preventDefault();
        this.close();
      }
    };

    onBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const trigger = event.target.querySelector(OPEN_SELECTOR);
      if (trigger instanceof HTMLElement) {
        this.open(trigger.getAttribute('data-vela-drawer-open'), trigger);
      }
    };

    onDocumentKeydown = (event) => {
      if (!this.activeLayer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.activeLayer.querySelector('.vela-info-drawer__panel')?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    getFocusableElements() {
      if (!this.activeLayer) return [];
      return Array.from(
        this.activeLayer.querySelectorAll(
          'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element instanceof HTMLElement && element.getClientRects().length > 0);
    }

    hydrateDeferredContent(layer) {
      const template = layer.querySelector('[data-vela-drawer-deferred-template]');
      const container = layer.querySelector('[data-vela-drawer-deferred-content]');
      if (!(template instanceof HTMLTemplateElement) || !(container instanceof HTMLElement)) return;

      const content = template.content.cloneNode(true);

      if (content instanceof DocumentFragment) {
        content.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
          image.decoding = 'async';
        });

        content.querySelectorAll('iframe').forEach((iframe) => {
          iframe.loading = 'lazy';
        });

        content.querySelectorAll('video').forEach((video) => {
          if (!video.hasAttribute('preload')) video.preload = 'metadata';
        });
      }

      container.append(content);
      template.remove();
    }

    open(id, trigger) {
      if (!id) return;
      const layer = document.getElementById(id);
      if (!(layer instanceof HTMLElement) || !this.contains(layer)) return;

      window.clearTimeout(this.closeTimer);

      if (this.activeLayer && this.activeLayer !== layer) {
        this.finishClose({ restoreFocus: false });
      }

      this.activeLayer = layer;
      this.activeTrigger = trigger instanceof HTMLElement ? trigger : null;
      this.activeTrigger?.setAttribute('aria-expanded', 'true');
      this.hydrateDeferredContent(layer);
      layer.hidden = false;
      layer.setAttribute('aria-hidden', 'false');
      scrollLock.lock(this);

      document.addEventListener('keydown', this.onDocumentKeydown);

      // Two frames guarantee the browser paints the off-canvas starting state.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.activeLayer !== layer) return;
          layer.classList.add('is-open');
          const closeButton = layer.querySelector('.vela-info-drawer__close');
          if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
        });
      });

      this.dispatchEvent(new CustomEvent('vela-info-drawer:open', { bubbles: true }));
    }

    close() {
      const layer = this.activeLayer;
      if (!layer) return;

      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onDocumentKeydown);

      const panel = layer.querySelector('.vela-info-drawer__panel');
      let finished = false;
      const complete = (event) => {
        if (finished) return;
        if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
        finished = true;
        panel?.removeEventListener('transitionend', complete);
        window.clearTimeout(this.closeTimer);
        this.finishClose({ restoreFocus: true });
      };

      panel?.addEventListener('transitionend', complete);
      this.closeTimer = window.setTimeout(() => complete(), 450);
      this.dispatchEvent(new CustomEvent('vela-info-drawer:close', { bubbles: true }));
    }

    finishClose({ restoreFocus }) {
      const layer = this.activeLayer;
      const trigger = this.activeTrigger;

      window.clearTimeout(this.closeTimer);
      document.removeEventListener('keydown', this.onDocumentKeydown);

      if (layer) {
        layer.classList.remove('is-open');
        layer.setAttribute('aria-hidden', 'true');
        layer.hidden = true;
      }

      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      this.activeLayer = null;
      this.activeTrigger = null;
      scrollLock.unlock(this);

      if (restoreFocus && trigger?.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    }
  }

  if (!customElements.get('vela-information-drawers')) {
    customElements.define('vela-information-drawers', VelaInformationDrawers);
  }
})();
